import { Button, Col, Row, Table, Typography, Popconfirm, Space, message, Input } from "antd"
import React, { useEffect, useState } from "react"
import PageHeader from "../../../components/Elements/PageHeader"
import { UnorderedListOutlined, DeleteOutlined } from "@ant-design/icons"
import NewTapePlateModal from "./NewTapePlateModal"
import moment from "moment/moment"
import { collection, onSnapshot, orderBy, query, doc, deleteDoc } from "firebase/firestore"
import { db } from "../../../firebase/config"

const { Text, Link } = Typography

export default function TapePlates(props) {
  const [plateData, setPlateData] = useState([])
  const [platesLoading, setPlatesLoading] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [lastPlate, setLastPlate] = useState({})
  const [searchText, setSearchText] = useState("")

  //gets data for tape plates
  useEffect(() => {
    setPlatesLoading(true)

    //ref for notifications
    const tapePlatesRef = collection(db, "tapePlatesList")

    // query
    const plateQuery = query(tapePlatesRef, orderBy("codeNumber", "desc"))

    //gets data with query
    const unsubscribe = onSnapshot(plateQuery, (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        results.push({ ...doc.data(), id: doc.id })
      })

      //sets notification data
      setPlateData(results)

      //last plate
      setLastPlate(results[0])

      //sets notification count
      setPlatesLoading(false)
    })
    //executes function
    return () => unsubscribe()
  }, [])

  const handleDeletePlate = async (record) => {
    try {
      await deleteDoc(doc(db, "tapePlatesList", record.id))
      message.success("Plate deleted successfully!")
    } catch (error) {
      console.error("Error deleting plate:", error)
      message.error("Failed to delete plate")
    }
  }

  const handleDeleteSelected = async () => {
    try {
      await Promise.all(selectedRowKeys.map((key) => deleteDoc(doc(db, "tapePlatesList", key))))
      message.success(`${selectedRowKeys.length} plate(s) deleted successfully!`)
      setSelectedRowKeys([])
    } catch (error) {
      console.error("Error deleting plates:", error)
      message.error("Failed to delete plates")
    }
  }

  // Build filter arrays from plate data
  const clientFilters =
    plateData &&
    plateData.reduce((accumulator, item) => {
      const clientName = item.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)

      if (existingItem) {
        existingItem.value = clientName
      } else if (clientName) {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }

      return accumulator
    }, [])

  const productFilters =
    plateData &&
    plateData.reduce((accumulator, item) => {
      const productName = item.product
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)

      if (existingItem) {
        existingItem.value = productName
      } else if (productName) {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }

      return accumulator
    }, [])

  const tapeColumns = [
    {
      title: "Code",
      dataIndex: "code",
      align: "left",
      width: "5%",
    },
    {
      title: "Client",
      dataIndex: "client",
      align: "left",
      width: "15%",
      ellipsis: true,
      filters: clientFilters,
      onFilter: (value, record) => record.client === value,
      filterSearch: true,
    },
    {
      title: "Product",
      dataIndex: "product",
      align: "left",
      width: "20%",
      ellipsis: true,
      filters: productFilters,
      onFilter: (value, record) => record.product === value,
      filterSearch: true,
    },
    {
      title: "Important",
      dataIndex: "important",
      align: "left",

      width: "7%",
    },
    {
      title: "Rows",
      dataIndex: "rows",
      align: "left",
      width: "5%",
    },
    {
      title: "Location",
      dataIndex: "plateLocation",
      align: "left",
      width: "10%",
    },
    {
      title: "Layout Design",
      dataIndex: "layoutDesign",
      align: "left",
      ellipsis: true,
      width: "18%",
    },
    {
      title: "Technical",
      dataIndex: "technicalPage",
      align: "left",
      width: "8%",
      render: (value) => {
        return value ? (
          <Link href={value} target="_blank">
            View
          </Link>
        ) : (
          <Text type="secondary">—</Text>
        )
      },
    },
    {
      title: "Created",
      dataIndex: "created",
      align: "left",
      width: "8%",
      sorter: (a, b) => a.created.seconds - b.created.seconds,
      render: (value) => <Text>{value && moment.unix(value.seconds).format("DD/MM/YYYY")}</Text>,
    },
    {
      title: "Delete",
      align: "left",
      width: "8%",
      render: (value, record) => (
        <Popconfirm
          title="Delete Plate"
          description="Are you sure you want to delete this plate?"
          onConfirm={() => handleDeletePlate(record)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: true }}>
          <Button type="primary" danger size="small" icon={<DeleteOutlined />}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  }

  const filteredData = plateData.filter((plate) => {
    return (
      !searchText ||
      (plate.code && plate.code.toLowerCase().includes(searchText.toLowerCase()))
    )
  })

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader text="Tape plates" icon={<UnorderedListOutlined />} />
        </Col>
        <Col span={24}>
          <NewTapePlateModal lastPlate={lastPlate} />
        </Col>

        <Col span={24}>
          <Input.Search
            placeholder="Search by code"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ marginBottom: 16 }}
          />
        </Col>

        {selectedRowKeys.length > 0 && (
          <Col span={24}>
            <Button type="primary" danger onClick={handleDeleteSelected}>
              Delete Selected ({selectedRowKeys.length})
            </Button>
          </Col>
        )}

        <Col span={24}>
          <Table
            size="small"
            loading={platesLoading}
            rowKey={(record) => record.id}
            columns={tapeColumns}
            dataSource={filteredData}
            bordered
            rowSelection={rowSelection}
            scroll={{ y: "calc(100vh - 300px)" }}
            pagination={false}
          />
        </Col>
      </Row>
    </div>
  )
}
